import { useEffect, useRef } from "react";
import styled from "styled-components";
import { useT } from "@sablier/v2-locales";
import { _, dayjs } from "@sablier/v2-mixins";
import scrollIntoViewIfNeeded from "scroll-into-view-if-needed";
import Item from "./Item";

const Wrapper = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 160px;
    height: 100%;
    padding: 2px;
    column-gap: 2px;
    border-radius: 6px;
    background-color: ${(props) => props.theme.colors.border};
  }
`;

const Column = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    height: 100%;
    border-radius: 4px;
    background-color: ${(props) => props.theme.colors.dark150};
    overflow: auto;
    appearance: none;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const Sticky = styled.div`
  width: 100%;
  padding: calc(${(props) => props.theme.sizes.edge} * 1) 0;
  border-bottom: 2px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.dark200};
  & > p {
    ${(props) => props.theme.styles.textButtonMini}
    & {
      color: ${(props) => props.theme.colors.gray400};
      text-align: center;
    }
  }
`;

const Elements = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    width: 100%;
    overflow: auto;
    appearance: none;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const HOURS = Array.from(Array(24).keys());
const MINUTES = Array.from(Array(60).keys());

interface Props {
  value?: dayjs.Dayjs;
  onChange: (value: number, unit: "hour" | "minute") => void;
}

function Time({ value, onChange }: Props) {
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const { t } = useT();

  useEffect(() => {
    if (hourRef.current) {
      const element = hourRef.current.querySelector<HTMLElement>(
        '[data-selected="true"]',
      );
      if (element) {
        scrollIntoViewIfNeeded(element, {
          scrollMode: "if-needed",
        });
      }
    }

    if (minuteRef.current) {
      const element = minuteRef.current.querySelector<HTMLElement>(
        '[data-selected="true"]',
      );
      if (element) {
        scrollIntoViewIfNeeded(element, {
          scrollMode: "if-needed",
        });
      }
    }
  }, [value]);

  return (
    <Wrapper>
      <Column>
        <Sticky>
          <p>{_.capitalize(t("words.hour"))}</p>
        </Sticky>
        <Elements ref={hourRef}>
          {HOURS.map((hour) => (
            <Item
              isSelected={hour === value?.hour()}
              key={hour}
              onClick={(value) => onChange(value, "hour")}
              value={hour}
            />
          ))}
        </Elements>
      </Column>
      <Column>
        <Sticky>
          <p>{_.capitalize(t("words.minutes"))}</p>
        </Sticky>
        <Elements ref={minuteRef}>
          {MINUTES.map((minute) => (
            <Item
              isSelected={minute === value?.minute()}
              key={minute}
              onClick={(value) => onChange(value, "minute")}
              value={minute}
            />
          ))}
        </Elements>
      </Column>
    </Wrapper>
  );
}

export default Time;
